import React, { useState } from 'react';
import './styles.css';

// Enhanced Quantum Layout Selector with collapsible, dockable UI
const QuantumLayoutSelector = ({ 
  currentLayout, 
  isTransforming, 
  transformToLayout, 
  resetToDefaultLayout,
  setAsDefaultLayout,
  availableLayouts 
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDocked, setIsDocked] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  
  const handleLayoutSelect = async (layoutId) => {
    if (isTransforming || currentLayout === layoutId) return;
    await transformToLayout(layoutId);
  };
  
  const handleSetAsDefault = async () => {
    if (isTransforming || !setAsDefaultLayout) return; // Add null check
    
    if (window.confirm(`Set "${availableLayouts[currentLayout]?.name}" as default layout?`)) {
      await setAsDefaultLayout();
    }
  };
  
  // If minimized, show only a small toggle button
  if (isMinimized) {
    return (
      <div className="quantum-layout-minimized">
        <button 
          className="expand-btn"
          onClick={() => setIsMinimized(false)}
          title="Expand Layout Selector"
        >
          🎨 {isTransforming ? 'Transforming...' : 'Layouts'}
        </button>
      </div>
    );
  }
  
  // If collapsed, show only header
  if (isCollapsed) {
    return (
      <div className={`quantum-layout-selector collapsed ${isDocked ? 'docked' : 'floating'}`}>
        <div className="selector-header">
          <button 
            className="toggle-btn"
            onClick={() => setIsCollapsed(false)}
          >
            <h4>🎨 Quantum Layouts</h4>
          </button>
          <div className="header-controls">
            <button 
              className="control-btn minimize-btn"
              onClick={() => setIsMinimized(true)}
              title="Minimize"
            >
              ⎯
            </button>
            <button 
              className="control-btn dock-btn"
              onClick={() => setIsDocked(!isDocked)}
              title={isDocked ? "Undock" : "Dock"}
            >
              {isDocked ? '🔗' : '📌'}
            </button>
            <button 
              className="control-btn close-btn"
              onClick={() => setIsCollapsed(true)}
              title="Collapse"
            >
              {isCollapsed ? '▶' : '▼'}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`quantum-layout-selector ${isDocked ? 'docked' : 'floating'}`}>
      <div className="selector-header">
        <div className="header-left">
          <h4 className="layout-title">🎨 Quantum Layouts</h4>
          <div className="layout-status">
            <span className={`status-dot ${isTransforming ? 'transforming' : 'idle'}`} />
            {isTransforming ? 'Transforming...' : 'Ready'}
          </div>
        </div>
        
        <div className="header-controls">
          <button 
            className="control-btn minimize-btn"
            onClick={() => setIsMinimized(true)}
            title="Minimize"
          >
            ⎯
          </button>
          <button 
            className="control-btn dock-btn"
            onClick={() => setIsDocked(!isDocked)}
            title={isDocked ? "Undock" : "Dock"}
          >
            {isDocked ? '🔗' : '📌'}
          </button>
          <button 
            className="control-btn close-btn"
            onClick={() => setIsCollapsed(true)}
            title="Collapse"
          >
            ▼
          </button>
        </div>
      </div>
      
      <div className="layout-grid">
        {Object.entries(availableLayouts).map(([id, layout]) => (
          <LayoutCard
            key={id}
            layout={layout}
            isActive={currentLayout === id}
            isDisabled={isTransforming}
            onClick={() => handleLayoutSelect(id)}
          />
        ))}
      </div>
      
      <div className="layout-actions">
        <div className="action-buttons">
          <button 
            className="action-btn reset-layout-btn"
            onClick={resetToDefaultLayout}
            disabled={isTransforming || currentLayout === 'default'}
            title="Reset to original layout"
          >
            🔄 Reset
          </button>
          {setAsDefaultLayout && ( // Only show if function exists
            <button 
              className="action-btn set-default-btn"
              onClick={handleSetAsDefault}
              disabled={isTransforming || currentLayout === 'default'}
              title="Set current layout as default"
            >
              ⭐ Set as Default
            </button>
          )}
        </div>
        
        <div className="layout-tips">
          <small>💡 Each layout optimizes visibility for different analysis purposes</small>
          <small>🎯 Click a layout to transform the visualization</small>
        </div>
      </div>
    </div>
  );
};

const LayoutCard = ({ layout, isActive, isDisabled, onClick }) => (
  <div 
    className={`layout-card ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
    onClick={!isDisabled ? onClick : undefined}
    title={layout.description}
  >
    <div className="layout-icon">{layout.icon}</div>
    <div className="layout-info">
      <div className="layout-name">{layout.name}</div>
      <div className="layout-complexity">
        {'★'.repeat(Math.ceil(layout.complexity * 5))}
      </div>
    </div>
    {isActive && (
      <div className="active-indicator">
        <div className="pulse-ring"></div>
        ✓
      </div>
    )}
  </div>
);


export default QuantumLayoutSelector;